import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { cartService } from '../services';

interface CartItem {
  _id: string;
  dishId: string;
  restaurantId: string;
  quantity: number;
  [key: string]: any;
}

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  isLoading: boolean;
  fetchCart: () => Promise<void>;
  addToCart: (
    dishId: string,
    restaurantId: string,
    quantity: number,
    options?: { variantId?: string | null; addons?: any[] }
  ) => Promise<void>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  updateCartItem: (dishId: string, restaurantId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  getCartItemQuantity: (dishId: string, restaurantId: string) => number;
  getCartItemId: (dishId: string, restaurantId: string) => string | null;
  getCartRestaurantId: () => string | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch cart from API
  const fetchCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: any = await cartService.getCart();
      if (response && response.data && Array.isArray(response.data)) {
        setCartItems(response.data);
      }
    } catch (error) {
      setCartItems([]);
      console.error('Failed to fetch cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add item to cart
  const addToCart = useCallback(async (
    dishId: string,
    restaurantId: string,
    quantity: number,
    options?: { variantId?: string | null; addons?: any[] }
  ) => {
    try {
       await cartService.addToCart({
        dishId,
        restaurantId,
        quantity,
        ...(options?.variantId !== undefined
          ? { variantId: options.variantId }
          : {}),
        ...(options?.addons ? { addons: options.addons } : {}),
      });
      
      // Refresh cart after adding
      await fetchCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  }, [fetchCart]);

  // Remove item from cart
  const removeFromCart = useCallback(async (cartItemId: string) => {
    try {
      await cartService.removeCartItem(cartItemId);
      // Update local state immediately for better UX
      setCartItems((prev) => prev.filter((item) => item._id !== cartItemId));
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  }, []);

  // Update cart item quantity
  const updateCartItem = useCallback(async (dishId: string, restaurantId: string, quantity: number) => {
    try {
      if (quantity === 0) {
        // Remove item if quantity is 0
        const cartItem = cartItems.find(
          (item) => item.dishId === dishId && item.restaurantId === restaurantId
        );
        if (cartItem) {
          await removeFromCart(cartItem._id);
        }
      } else {
        await cartService.addToCart({
          dishId,
          restaurantId,
          quantity,
        });
        await fetchCart();
      }
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    }
  }, [cartItems, fetchCart, removeFromCart]);

  // Clear entire cart
  const clearCart = useCallback(async () => {
    try {
      await cartService.clearCart();
      setCartItems([]);
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  }, []);

  // Get quantity of a specific item in cart
  const getCartItemQuantity = useCallback((dishId: string, restaurantId: string): number => {
    const item = cartItems.find(
      (cartItem) => cartItem.dishId === dishId && cartItem.restaurantId === restaurantId
    );
    return item ? item.quantity : 0;
  }, [cartItems]);

  // Get cart item ID for a specific dish
  const getCartItemId = useCallback((dishId: string, restaurantId: string): string | null => {
    const item = cartItems.find(
      (cartItem) => cartItem.dishId === dishId && cartItem.restaurantId === restaurantId
    );
    return item ? item._id : null;
  }, [cartItems]);

  // Get restaurant ID from cart (returns null if cart is empty)
  const getCartRestaurantId = useCallback((): string | null => {
    if (cartItems.length === 0) {
      return null;
    }
    return cartItems[0].restaurantId;
  }, [cartItems]);

  // Calculate total cart count
  const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 0), 0);

  // Fetch cart on mount
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Listen for cart update events from other components (for backward compatibility)
  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCart();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [fetchCart]);

  const value: CartContextType = {
    cartItems,
    cartCount,
    isLoading,
    fetchCart,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    getCartItemQuantity,
    getCartItemId,
    getCartRestaurantId,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
